import { useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RowListSkeleton } from "@/components/RowListSkeleton";
import { FormActions, Toolbar } from "@jbnl/shared-ui";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import {
  TEXT_FIELD,
  maxTextLength,
  textFieldLabel,
  useCreateTextPreset,
  useDeleteTextPreset,
  useTextPresets,
  useUpdateTextPreset,
  type TextField,
  type TextPreset,
} from "./useTextPresets";

const FIELDS = [
  TEXT_FIELD.briefing,
  TEXT_FIELD.reportingTime,
  TEXT_FIELD.remarks,
] as const;

/** One card per field: a preset only ever offers itself on the field it targets (#177). */
export function TextPresets() {
  const { data: presets, isPending } = useTextPresets();

  return (
    <div className="space-y-6">
      {FIELDS.map((field) => (
        <TextFieldSection
          key={field}
          field={field}
          presets={presets?.filter((preset) => preset.field === field) ?? []}
          isPending={isPending}
        />
      ))}
    </div>
  );
}

function TextFieldSection({
  field,
  presets,
  isPending,
}: {
  field: TextField;
  presets: TextPreset[];
  isPending: boolean;
}) {
  const { t } = useTranslation("standaardteksten");

  return (
    <Card>
      <CardHeader>
        <CardTitle>{textFieldLabel(field)}</CardTitle>
      </CardHeader>
      <CardContent>
        <Toolbar>
          <NewPreset field={field} />
        </Toolbar>

        {isPending ? (
          <RowListSkeleton actions={["w-20", "w-24"]} />
        ) : presets.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("leeg")}</p>
        ) : (
          <ul className="divide-y">
            {presets.map((preset) => (
              <li
                key={preset.id}
                className="flex items-center justify-between gap-3 py-2"
              >
                <span className="min-w-0 flex-1 break-words">
                  {preset.text}
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  <EditPreset preset={preset} />
                  <DeletePreset preset={preset} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function NewPreset({ field }: { field: TextField }) {
  const { t } = useTranslation("standaardteksten");
  const create = useCreateTextPreset();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus />
          {t("toevoegen")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("toevoegen")}</DialogTitle>
        </DialogHeader>
        <PresetForm
          key={String(open)}
          field={field}
          submitLabel={t("submitToevoegen")}
          pending={create.isPending}
          error={create.error}
          onSubmit={(text) =>
            create.mutate({ field, text }, { onSuccess: () => setOpen(false) })
          }
        />
      </DialogContent>
    </Dialog>
  );
}

function EditPreset({ preset }: { preset: TextPreset }) {
  const { t } = useTranslation(["standaardteksten", "common"]);
  const update = useUpdateTextPreset();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          {t("wijzigen")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("wijzigen")}</DialogTitle>
        </DialogHeader>
        <PresetForm
          key={String(open)}
          field={preset.field}
          text={preset.text}
          submitLabel={t("common:actions.save")}
          pending={update.isPending}
          error={update.error}
          onSubmit={(text) =>
            update.mutate(
              { id: preset.id, text },
              { onSuccess: () => setOpen(false) },
            )
          }
        />
      </DialogContent>
    </Dialog>
  );
}

function DeletePreset({ preset }: { preset: TextPreset }) {
  const { t } = useTranslation("standaardteksten");
  const remove = useDeleteTextPreset();

  return (
    <ConfirmDelete
      trigger={
        <Button variant="ghost" size="sm" disabled={remove.isPending}>
          {t("verwijderen")}
        </Button>
      }
      title={t("verwijderVraag")}
      description={t("verwijderDescription")}
      confirmLabel={t("verwijderConfirm")}
      onConfirm={() => remove.mutate(preset.id)}
    />
  );
}

function PresetForm({
  field,
  text,
  submitLabel,
  pending,
  error,
  onSubmit,
}: {
  field: TextField;
  text?: string;
  submitLabel: string;
  pending: boolean;
  error: Error | null;
  onSubmit: (text: string) => void;
}) {
  const { t } = useTranslation(["standaardteksten", "common"]);
  const [value, setValue] = useState(text ?? "");
  const maxLength = maxTextLength(field);

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(value.trim());
      }}
    >
      <Field>
        <FieldLabel htmlFor="preset-tekst">{t("tekst")}</FieldLabel>
        {/* The same input the wedstrijddag form itself uses per field (#177): a single line
            for Briefing/Meldtijd, a textarea only where Opmerkingen allows more room. */}
        {field === TEXT_FIELD.remarks ? (
          <Textarea
            id="preset-tekst"
            rows={3}
            value={value}
            maxLength={maxLength}
            required
            onChange={(e) => setValue(e.target.value)}
          />
        ) : (
          <Input
            id="preset-tekst"
            value={value}
            maxLength={maxLength}
            required
            onChange={(e) => setValue(e.target.value)}
          />
        )}
      </Field>

      <FormActions>
        {error && <p className="text-sm text-destructive">{error.message}</p>}
        <Button type="submit" disabled={pending || value.trim().length === 0}>
          {pending ? t("common:actions.busy") : submitLabel}
        </Button>
      </FormActions>
    </form>
  );
}
